import { FC, useMemo } from 'react';
import { PieChart, PieChartProps } from 'react-minimal-pie-chart';

import { sumBy } from 'lodash';

export const EMPTY_CHART_COLOR = 'rgba(241,241,241, .2)';

export const emptyChartProps = {
  rounded: false,
  data: [{ key: 'empty', value: 100, color: EMPTY_CHART_COLOR }],
  label: undefined,
  labelStyle: undefined,
  paddingAngle: 0,
};

export type PieChartData = { key: string; value: number; color: string }[];

interface AdvancedPieChartProps extends Omit<PieChartProps, 'data'> {
  showLabels?: boolean;
  labelFontSize?: number;
  data: PieChartData;
}

export const AdvancedPieChart: FC<AdvancedPieChartProps> = ({
  data,
  labelFontSize = 12,
  ...props
}) => {
  const pieChartData: { key: string; value: number; color: string }[] = useMemo(() => {
    const total = sumBy(data, 'value');

    return data
      .filter(({ value }) => value)
      .map(({ key, value, color }) => ({
        key,
        value: Math.round((value / total) * 100),
        color,
      }));
  }, [data]);

  const labelProps = ({ showLabels }: { showLabels: boolean }) => {
    if (showLabels) {
      return {
        label: ({ dataEntry }) => `${dataEntry.value}%`,
        labelStyle: (index) => ({
          fill: pieChartData[index]?.color,
          fontSize: `${labelFontSize}px`,
          fontWeight: 'bold',
          fontFamily: 'Rubik',
          letterSpacing: 1,
        }),
      };
    }

    return {};
  };

  return (
    <PieChart
      rounded
      lineWidth={25}
      radius={50}
      data={pieChartData}
      paddingAngle={25}
      {...(pieChartData.length > 0 ? {} : emptyChartProps)}
      {...labelProps({ showLabels: props?.showLabels })}
      {...props}
    />
  );
};
